import React from 'react';
import { Justify, SearchBox, TabPanel, Tabs, StatusTip } from 'tea-component';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import { ClassSpecial } from '@op/constants/common';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { LanguageType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { VideoPoint } from '@opm/operationLog/pages';
import useIntls from '@opm/hooks/useIntls';
import { useScrollBottomRegister } from '@opm/components/ScrollBottomList';
import { useDouyinVideoData } from '../../hooks';
import { useVideoTranslate } from '../../context';
import VideoCard from '../../components/videoCard';
import DouyinVideoTable from './DouyinVideoTable';
import { DOUYIN_SORT_OPTIONS } from './constants';
import './index.scss';

interface IDouyinTabProps {
  backCondConfig: any;
  translateCategory: (value: string, isEn: boolean) => string;
}

export default function DouyinTab({
  backCondConfig,
}: IDouyinTabProps) {
  DBOM_POINT.useInitPage({ buriedPoint: VideoPoint, pageExtInfo: 'douyin' });

  const { intl } = useIntls();
  const { language, setLanguage } = useVideoTranslate();

  const { filterRef, setFilter, loading, ...filterProps } = useFilterCommon({
    keys: ['MultiDateRange', 'VideoDouyinCategory'],
  });

  const {
    cardRecords,
    tableRecords,
    total,
    max,
    pageInfo,
    changePageInfo,
    nextPage,
    resetData,
    operation,
    setOperation,
    sortBy,
    setSortBy,
    setKeyword,
    download,
    loading: dataLoading,
  } = useDouyinVideoData({
    filter: filterRef.current,
  });

  useScrollBottomRegister(nextPage);

  const handleConfirm = (value: any) => {
    setFilter(value);
    resetData();
  };

  return (
    <MarketContentBody
      loading={loading}
      filterSlot={
        <FilterCommon
          {...filterProps}
          userDefaultValue={{
            ...DBO_TM.filterRangeDay(6, 0),
            date_type: 'daily',
          }}
          realTimeMode
          confirm={handleConfirm}
          backCondConfig={backCondConfig}
        />
      }
    >
      <CardCommon
        className="dbom-douyin-tab-card"
        alwaysShowOperations
        defaultOperation={operation}
        operations={[
          {
            element: (
              <SearchBox
                onSearch={(value = '') => {
                  setKeyword(value);
                }}
                onClear={() => {
                  setKeyword('');
                }}
                placeholder={intl({ id: 'Search video' })}
              ></SearchBox>
            ),
          },
          cardIcon,
          tableIcon,
        ]}
        hasDownload={total > 0}
        title={
          <div>
            {operation === 'card' && (
              <SortBySelect
                key="sortBy"
                defaultValue={sortBy?.by}
                onChange={(_key: any, value: any) => {
                  setSortBy({ order: 'desc', by: value });
                }}
                defaultParams={{
                  btnTextPrefix: intl({ id: 'Sort By' }),
                  options: DOUYIN_SORT_OPTIONS,
                }}
              />
            )}
            <SingleTranslateSelect
              defaultValue={language}
              onChange={(key: string, value: LanguageType) => setLanguage(value)}
            ></SingleTranslateSelect>
          </div>
        }
        handleOperationsOnClick={(item: any) => {
          setOperation(item.symbol);
        }}
        handleDownloadOnClick={download}
      >
        <Tabs activeId={operation} className={ClassSpecial.TAB_NONE}>
          <TabPanel id="card">
            <DouyinVideoCardList
              language={language}
              cardRecords={cardRecords}
              loading={dataLoading}
            />
          </TabPanel>
          <TabPanel id="table">
            <DouyinVideoTable
              sortBy={sortBy}
              setSortBy={setSortBy}
              language={language}
              tableRecords={tableRecords}
              loading={dataLoading}
              total={total}
              pageInfo={pageInfo}
              changePageInfo={changePageInfo}
              max={max}
            />
          </TabPanel>
        </Tabs>
      </CardCommon>
    </MarketContentBody>
  );
}

interface IDouyinVideoCardListProps {
  language: LanguageType;
  cardRecords: any[];
  loading?: boolean;
}

/** 卡片视图 — 与 TikTok Tab 相同 VideoCard 组件 */
function DouyinVideoCardList({
  language,
  cardRecords,
  loading,
}: IDouyinVideoCardListProps) {
  const { intl } = useIntls();

  return (
    <CardCommon
      contentCard
      loading={loading && cardRecords.length === 0}
      loadingHeight={350}
      empty={cardRecords.length === 0}
      emptyHeight={350}
    >
      <>
        <div className="dbom-video-list">
          {cardRecords.map((item: any) => (
            <VideoCard
              key={item.url || item.video_id}
              item={item}
              language={language}
              hideKeys={['views', 'comments']}
            />
          ))}
        </div>
        {loading && cardRecords.length !== 0 && (
          <div className="loading-common dbom-loading-common">
            <StatusTip status="loading" loadingText={intl('Loading...')} />
          </div>
        )}
      </>
    </CardCommon>
  );
}
